/**
 * Nueva campaña · Segmentar (DISENO §5, ARQUITECTURA §3). Del equipo.
 *
 * - `/activa/segmentaciones/nueva`: «Antes de empezar» (la base en uso y el % de control) y «Segmentar ahora». Con la
 *   base vieja, antes pregunta (Actualizar base / Segmentar igual). Sin base, manda a subirla. El % de control arranca en
 *   el de Descuentos de la marca (fase 3) y se puede cambiar para esta campaña.
 * - `/activa/segmentaciones/:id`: las etapas avanzando mientras el servidor arma los grupos (la pantalla consulta cada
 *   2 s y se puede salir y volver) y, al terminar, directo a Grupos (Alan, 02/11). Abierta ya terminada (el paso
 *   «Segmentar» desde Grupos o Mensajes), cuántos grupos se encontraron y «Ver los grupos».
 * - Con la base en uso, «Subir grupos armados (Excel)»: la campaña nace con los grupos de cada hoja y el control del
 *   Excel, sin segmentar, y va directo a Grupos.
 * - Los canales conectados y el contexto de la fecha (DISENO §5) todavía no se muestran.
 * - Arriba, la marca sobre la que se trabaja («Segmentando la base de HATSU»): para no segmentar la de otro cliente.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { num } from "../../campana-nueva/utils/format";
import { baseVentasApi } from "../../base-ventas/api";
import { R_SUBIR_BASE, diaMes } from "../../base-ventas/logic/base";
import type { EstadoBase } from "../../base-ventas/types";
import { descuentosApi } from "../../descuentos/api";
import type { Descuentos } from "../../descuentos/types";
import { usePedido } from "../../emails/hooks/usePedido";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { usePolling } from "../../wa-oficial/hooks/usePolling";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { segmentacionesApi } from "../api";
import { Antetitulo, PasosCampana, btnPrimario, btnSecundario } from "../components/PasosCampana";
import { Pagina } from "../../../components/activa/Pagina";
import { MarcaDeTrabajo } from "../../campana-nueva/components/MarcaDeTrabajo";
import {
  CADA_MS_SEGMENTANDO,
  CONTROL_POR_DEFECTO,
  OPCIONES_CONTROL,
  R_NUEVA_CAMPANA,
  avance,
  etapasVisibles,
  nombreDelGrupo,
  rutaGrupos,
  rutaSegmentar,
} from "../logic/segmentar";
import type { Segmentacion } from "../types";

function Encabezado() {
  return (
    <div className="space-y-1.5">
      <Antetitulo>Paso 1 de 4</Antetitulo>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Segmentar</h1>
      <p className="text-sm sm:text-base text-slate-600">DEENEX arma los grupos con tu base de hoy. Cada segmentación es una campaña nueva.</p>
    </div>
  );
}

/** «¿Seguro?» con la base vieja: un diálogo de verdad, con el foco adentro. */
function DialogoBaseVieja({ dia, onActualizar, onSeguir, onCerrar }: { dia: string; onActualizar: () => void; onSeguir: () => void; onCerrar: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d || d.open) return;
    if (typeof d.showModal === "function") d.showModal();
    else d.setAttribute("open", "");
  }, []);
  return (
    <dialog ref={ref} aria-labelledby="base-vieja-titulo" onCancel={onCerrar} className="max-w-md rounded-2xl p-0 backdrop:bg-slate-900/40">
      <div className="space-y-4 p-6">
        <h2 id="base-vieja-titulo" className="text-lg font-semibold text-slate-900">
          Tu base es del {dia}
        </h2>
        <p className="text-sm text-slate-600">Si alguien compró desde entonces, le puede llegar un cupón que no necesita.</p>
        <div className="flex flex-wrap gap-3">
          <button type="button" className={btnPrimario} onClick={onActualizar}>
            Actualizar base
          </button>
          <button type="button" className={btnSecundario} onClick={onSeguir}>
            Segmentar igual
          </button>
        </div>
      </div>
    </dialog>
  );
}

function AntesDeEmpezar() {
  const navigate = useNavigate();
  const base = usePedido<EstadoBase>(() => baseVentasApi.estado(), "base-ventas");
  const reglas = usePedido<Descuentos>(() => descuentosApi.ver(), "descuentos");
  // el % elegido acá; mientras no se elija otro, el de Descuentos (si no se pudo leer, el 15%). Se calcula en cada
  // render: copiado con un useEffect, si Descuentos llegaba junto con la base el efecto podía correr después de que se
  // eligiera otro % y pisarlo (SegmentarPage.carrera.test.tsx).
  const [elegido, setElegido] = useState<number | null>(null);
  const pctMarca = reglas.dato?.pctControl;
  const pct = elegido ?? (pctMarca && OPCIONES_CONTROL.includes(pctMarca) ? pctMarca : CONTROL_POR_DEFECTO);
  const [preguntar, setPreguntar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<{ texto: string; otra?: string } | null>(null);
  const e = base.dato;
  const enUso = e?.enUso ?? null;

  const segmentar = async () => {
    setPreguntar(false);
    setEnviando(true);
    setError(null);
    const l = await llamar<Segmentacion>(() => segmentacionesApi.crear(pct));
    setEnviando(false);
    if (l.ok) return navigate(rutaSegmentar(l.data.id), { replace: true, state: { recienLanzada: true } });
    const otra = typeof l.extra.segmentacionId === "string" ? l.extra.segmentacionId : undefined;
    setError({ texto: textoError(l), otra });
  };

  return (
    <div className="space-y-6">
      <Encabezado />
      {base.error && !e && <ErrorTarjeta texto={base.error} onReintentar={base.recargar} />}
      {base.cargando && !e && <Esqueleto alto="h-40" etiqueta="Cargando tu base" />}
      {e && !enUso && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="sin-base">
          <p className="text-base font-semibold text-slate-900">Primero subí tu base de ventas</p>
          <p className="text-sm text-slate-600">Con las ventas y los clientes, DEENEX arma los grupos.</p>
          <Link to={R_SUBIR_BASE} className={btnPrimario}>
            Subir base de datos
          </Link>
        </div>
      )}
      {enUso && (
        <section aria-labelledby="antes-titulo" className="space-y-4">
          <h2 id="antes-titulo" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Antes de empezar
          </h2>
          <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">Tu base</p>
                <p className="text-sm text-slate-600">
                  {num(enUso.personas)} personas · {num(enUso.pedidos)} pedidos
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${e?.vieja ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                  Del {diaMes(enUso.hasta)}
                  {e?.vieja && <span className="sr-only"> (desactualizada)</span>}
                </span>
                <Link to={R_SUBIR_BASE} className="text-sm font-semibold text-violet-700 hover:text-violet-900">
                  Actualizar
                </Link>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 p-4">
              <label htmlFor="pct-control" className="min-w-0">
                <span className="block font-semibold text-slate-900">Grupo de control</span>
                <span className="block text-sm text-slate-600">{pct}% de cada grupo no recibe nada, para medir</span>
              </label>
              <select
                id="pct-control"
                value={pct}
                onChange={(x) => setElegido(Number(x.target.value))}
                className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              >
                {OPCIONES_CONTROL.map((o) => (
                  <option key={o} value={o}>
                    {o}%
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error && (
            <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error.texto}{" "}
              {error.otra && (
                <Link to={rutaSegmentar(error.otra)} className="font-semibold underline">
                  Ver la que se está armando
                </Link>
              )}
            </div>
          )}
          <button type="button" className={btnPrimario} disabled={enviando} onClick={() => (e?.vieja ? setPreguntar(true) : void segmentar())}>
            {enviando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            Segmentar ahora
          </button>
          {preguntar && (
            <DialogoBaseVieja dia={diaMes(enUso.hasta)} onActualizar={() => navigate(R_SUBIR_BASE)} onSeguir={() => void segmentar()} onCerrar={() => setPreguntar(false)} />
          )}
        </section>
      )}
      {enUso && <SubirGruposArmados />}
    </div>
  );
}

/** El Excel entero, en el navegador, como lo acepta el servidor. */
const MAX_BYTES_EXCEL = 8 * 1024 * 1024;

const enBase64 = (archivo: File) =>
  new Promise<string>((ok, mal) => {
    const lector = new FileReader();
    lector.onload = () => ok(String(lector.result ?? "").replace(/^data:[^,]*,/, ""));
    lector.onerror = () => mal(lector.error);
    lector.readAsDataURL(archivo);
  });

/**
 * Los grupos ya armados en un Excel (una hoja por grupo, con el mail o el celular, y la del control con el «Grupo de
 * origen»): la campaña nace con esos grupos y su control, sin segmentar, y sigue en Grupos y Mensajes.
 */
function SubirGruposArmados() {
  const navigate = useNavigate();
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState("");
  const input = useRef<HTMLInputElement>(null);

  const subir = async (archivo: File | undefined) => {
    if (input.current) input.current.value = "";
    if (!archivo) return;
    setError("");
    if (!/\.xlsx?$/i.test(archivo.name)) return setError("Tiene que ser un Excel (.xlsx o .xls).");
    if (archivo.size > MAX_BYTES_EXCEL) return setError("El Excel pesa más de 8 MB. Sacale las hojas que no son grupos y probá de nuevo.");
    setSubiendo(true);
    let contenido: string;
    try {
      contenido = await enBase64(archivo);
    } catch {
      setSubiendo(false);
      return setError("No pudimos leer el archivo. Probá de nuevo.");
    }
    const l = await llamar<Segmentacion>(() => segmentacionesApi.importar(archivo.name, contenido));
    setSubiendo(false);
    if (l.ok) return navigate(rutaGrupos(l.data.id));
    setError(textoError(l));
  };

  return (
    <section aria-labelledby="importar-titulo" className="space-y-3 rounded-2xl border border-dashed border-slate-300 bg-white p-5" data-testid="importar-grupos">
      <h2 id="importar-titulo" className="text-base font-semibold text-slate-900">
        ¿Ya tenés los grupos armados?
      </h2>
      <p className="text-sm text-slate-600">
        Subí el Excel: cada hoja es un grupo, con el mail o el celular de cada persona, y la hoja del control dice en «Grupo de origen» de qué grupo es cada una. Buscamos a cada persona en tu base y la campaña queda lista para escribir los mensajes.
      </p>
      {error && (
        <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <label className={`${btnSecundario} cursor-pointer ${subiendo ? "pointer-events-none opacity-60" : ""}`} aria-disabled={subiendo || undefined}>
        {subiendo && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
        {subiendo ? "Subiendo los grupos…" : "Subir grupos armados (Excel)"}
        <input
          ref={input}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          className="sr-only"
          disabled={subiendo}
          data-testid="importar-archivo"
          onChange={(x) => void subir(x.target.files?.[0])}
        />
      </label>
    </section>
  );
}

const terminada = (s: Segmentacion) => s.estado === "borrador" || s.estado === "nombres_pendientes";

function Corrida({ id }: { id: string }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [s, setS] = useState<Segmentacion | null>(null);
  const [error, setError] = useState("");
  // la que se vio armando, o la recién lanzada desde «Segmentar ahora» (puede terminar antes de la primera consulta):
  // al terminar abre Grupos sin pasar por el resumen
  const armando = useRef((state as { recienLanzada?: boolean } | null)?.recienLanzada === true);
  const cargar = async () => {
    const l = await llamar<Segmentacion>(() => segmentacionesApi.ver(id));
    if (!l.ok) {
      setError(textoError(l));
      if (l.status === 404) setS(null);
      throw new Error(textoError(l));
    }
    if (l.data.estado === "procesando") armando.current = true;
    else if (armando.current && terminada(l.data)) {
      navigate(rutaGrupos(id), { replace: true });
      return;
    }
    setError("");
    setS(l.data);
  };
  useEffect(() => {
    setS(null);
    void cargar().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const polling = usePolling(cargar, s?.estado === "procesando" ? CADA_MS_SEGMENTANDO : null, [id, s?.estado]);

  if (!s) return error ? <ErrorTarjeta texto={error} onReintentar={() => void cargar().catch(() => undefined)} /> : <Esqueleto alto="h-64" etiqueta="Cargando la campaña" />;

  const etapas = etapasVisibles(s);
  const listo = terminada(s);
  return (
    <div className="space-y-6">
      <Encabezado />
      {s.estado === "procesando" && (
        <section aria-labelledby="segmentando-titulo" className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" aria-busy="true">
          <Antetitulo>Segmentando</Antetitulo>
          <h2 id="segmentando-titulo" className="text-xl font-semibold text-slate-900">
            Estamos armando tus grupos
          </h2>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={avance(s)} aria-label="Avance">
            <div className="h-full rounded-full bg-violet-600 transition-all" style={{ width: `${avance(s)}%` }} />
          </div>
          <ol className="space-y-3" data-testid="etapas">
            {etapas.map((e) => (
              <li key={e.etapa} aria-current={e.estado === "ahora" ? "step" : undefined} className={`flex items-center gap-3 ${e.estado === "falta" ? "opacity-50" : ""}`}>
                {e.estado === "hecha" ? (
                  <CheckCircle2 size={18} className="flex-none text-violet-600" aria-label="hecha" />
                ) : e.estado === "ahora" ? (
                  <Loader2 size={18} className="flex-none animate-spin text-violet-600" aria-label="en curso" />
                ) : (
                  <span aria-hidden="true" className="h-[18px] w-[18px] flex-none rounded-full border-2 border-slate-200" />
                )}
                <span className={e.grande ? `text-[15px] font-bold tracking-wide ${e.estado === "falta" ? "text-slate-900" : "text-violet-800"} ${e.estado === "ahora" ? "animate-pulse" : ""}` : "text-base font-medium text-slate-900"}>
                  {e.texto}
                </span>
                {e.detalle && e.estado !== "falta" && <span className="ml-auto text-sm text-slate-500 tabular-nums">{e.detalle}</span>}
              </li>
            ))}
          </ol>
          {polling.detenido && <ErrorTarjeta texto="Dejamos de consultar el avance." onReintentar={polling.reintentar} />}
        </section>
      )}
      {s.estado === "fallida" && (
        <section className="space-y-3 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="fallida">
          <p className="flex items-start gap-2 font-semibold text-red-800">
            <AlertTriangle size={18} className="mt-0.5 flex-none" aria-hidden="true" />
            {s.error?.texto || "No pudimos armar los grupos."}
          </p>
          {s.errorDetalle && <p className="text-xs text-red-700">Para el equipo: {s.errorDetalle}</p>}
          <Link to={R_NUEVA_CAMPANA} className={btnSecundario}>
            Volver a segmentar
          </Link>
        </section>
      )}
      {listo && (
        <section aria-labelledby="encontramos-titulo" className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="encontramos">
          <h2 id="encontramos-titulo" className="text-xl font-semibold text-slate-900">
            Encontramos {s.grupos.length} {s.grupos.length === 1 ? "grupo" : "grupos"}
          </h2>
          <p className="text-sm text-slate-600">{num(s.grupos.reduce((t, g) => t + g.personas, 0))} clientes que compraron, cada uno en su grupo.</p>
          <ul className="flex flex-wrap gap-2">
            {s.grupos.map((g, i) => (
              <li key={g.id} className="rounded-full bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-800">
                {nombreDelGrupo(g, i)} · {num(g.personas)}
              </li>
            ))}
          </ul>
          <Link to={rutaGrupos(s.id)} className={btnPrimario}>
            Ver los grupos
          </Link>
        </section>
      )}
    </div>
  );
}

export default function SegmentarPage() {
  const { id } = useParams();
  return (
    // el mismo contenedor que Grupos y Mensajes: el título y los pasos no saltan al pasar de un paso a otro
    <Pagina angosta>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-900">Nueva campaña</p>
        <MarcaDeTrabajo accion="Segmentando la base de" />
      </div>
      <PasosCampana actual={0} rutas={[null, id ? rutaGrupos(id) : null, null]} />
      {id ? <Corrida id={id} /> : <AntesDeEmpezar />}
    </Pagina>
  );
}
